/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: Presentational
 * Reason: The free placement tool - a caseworker drags signature rectangles onto a document that
 * carries no AcroForm fields and assigns each one a role, so a flat or scanned PDF can be signed at
 * all. UX REVIEW OUTSTANDING: the interaction was not specified by the business side (signed
 * descope D-7) and is the minimal defensible one.
 */
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';
import {pdfWorkerSrc} from '../../../../core/shared/helper/pdfjs-worker.helper';
import {SmAdHocPageDto} from '../../model/dto/api/sm-ad-hoc-page.dto';
import {SmAdHocRectDto} from '../../model/dto/api/sm-ad-hoc-rect.dto';
import {SmManualPlacementDto} from '../../model/dto/api/sm-manual-placement.dto';
import {SmOverlayRect} from '../../interface/sm-overlay-rect.interface';
import {SmPdfPageMeasurement} from '../../interface/sm-pdf-page-measurement.interface';
import {SmPlacementDraft} from '../../interface/sm-placement-draft.interface';
import {SmPlacementRole} from '../../interface/sm-placement-role.interface';
import {
    buildPageOverlay,
    clampBoxToPage,
    deviceRatioOf,
    fromCssPixels,
    isInsideCropBox,
    normaliseDragBox,
    toCssPixels,
    toOverlayRect,
    toPdfRect
} from '../sm-signature-pdf-guided/sm-signature-pdf-guided.geometry';

/** One placed rectangle as it is drawn on the page currently shown, already in CSS pixels. */
interface SmPlacementMarker {

    /** The draft this box belongs to, by reference - deleting the draft removes the box. */
    draft: SmPlacementDraft;

    /** Position of {@link draft} in the list, so the box can carry the same number the list shows. */
    ordinal: number;

    left: number;
    top: number;
    width: number;
    height: number;
}

/**
 * The smallest rectangle that may be placed, in PDF points.
 *
 * <p>
 * The backend refuses a rectangle with no extent at all, which catches a stray click but not a
 * three-point smudge - and a three-point signature box is worse than a refused one, because it is
 * accepted, stamped, and illegible. Twelve points is about four millimetres, comfortably below any
 * real signature box and comfortably above anything produced by a hand that slipped while clicking.
 * </p>
 */
export const SM_MIN_PLACEMENT_SIZE_PT: number = 12;

/**
 * Drag a signature rectangle onto a document that has no signature fields of its own.
 *
 * <h2>Why this exists at all</h2>
 * <p>
 * A flat, scanned or third-party PDF carries no AcroForm fields and no markers. The derivation
 * therefore finds no signature places, the process gets no slots, no signer has anything to sign,
 * and the whole signing route stops before it starts - with no error anywhere, because nothing went
 * wrong. This is the surface that puts the places there by hand, which is the entire content of
 * derivation mode {@code manualPlacement}.
 * </p>
 *
 * <h2>THE INTERACTION IS PROVISIONAL - UX REVIEW OUTSTANDING</h2>
 * <p>
 * No interaction concept was delivered for this tool; that is signed descope D-7. What is built here
 * is the minimal defensible one - pick a role, drag a box, see the list, delete a row, save - and it
 * is recorded as provisional rather than presented as decided. Anything beyond it (resizing a placed
 * box, dragging it, snapping, multi-select) is deliberately absent: guessing at an interaction
 * nobody specified produces a surface that is expensive to change and looks finished.
 * </p>
 *
 * <h2>The two guards against the cropped-away margin</h2>
 * <p>
 * A signature drawn into the part of the page a viewer does not render is INVISIBLE, and nothing
 * says so: the stamp succeeds and the document comes back looking unsigned. The backend does refuse
 * such a rectangle - but inside {@code stampSignatureImageAt}, i.e. at the moment the customer
 * signs, long after the caseworker who drew it has moved on. So it is caught here, twice:
 * </p>
 * <ol>
 *   <li>every drag is CUT to the rendered page ({@code clampBoxToPage}), which makes a rectangle
 *       outside the crop box impossible to draw rather than merely refused;</li>
 *   <li>every rectangle is then checked against the page's own crop box ({@code isInsideCropBox},
 *       with the backend's own half-point tolerance) before it enters the list, and the whole list
 *       is checked again before it is sent. A clamp is only as good as the page extent it was given,
 *       and the second check is what notices when that extent was wrong.</li>
 * </ol>
 *
 * <h2>No page whose geometry disagrees may be drawn on</h2>
 * <p>
 * The same cross-check the guided view performs (GR-8, capability matrix row B13), used for a
 * stricter purpose. There, a disagreement withholds the OVERLAY over existing fields. Here it
 * withholds DRAWING altogether: a rectangle drawn while the renderer and the server see different
 * views of the page would be stored in coordinates that mean something else on the server, and
 * unlike a misplaced marker it would persist. Refusing to draw is the only honest answer.
 * </p>
 *
 * <h2>What this component deliberately does NOT show</h2>
 * <p>
 * Anything about the evidence level or the authentication method (BA-2). Those follow from the
 * identification act the signer actually went through and are derived on the server; a surface that
 * displayed, hinted at or - worse - offered a choice of them would be making a claim it has no basis
 * for, at exactly the point where the claim is legally load-bearing. Placing a rectangle says where
 * a signature goes and nothing whatsoever about what that signature is worth.
 * </p>
 *
 * <h2>Sending, and who sends</h2>
 * <p>
 * This component emits; the host sends. That keeps the drawing surface free of the endpoint and lets
 * the host decide what a successful save means for the rest of its screen. The emitted list is
 * ALWAYS complete, because the endpoint reconciles rather than appends - see
 * {@code SmSigningApiService#savePlacements}.
 * </p>
 */
@Component({
    selector: 'mvs-sm-placement-tool',
    templateUrl: './sm-placement-tool.component.html',
    styleUrls: ['./sm-placement-tool.component.scss'],
    standalone: false
})
export class SmPlacementToolComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

    /** The bytes to draw on. The identity everything on screen is derived from. */
    @Input() document: Blob;

    /**
     * The per-page geometry the SERVER measured on the same bytes.
     *
     * Two uses, and only two: the cross-check before anything may be drawn, and the crop box every
     * rectangle is validated against. Never used to compute a position - that belongs to the
     * viewport, which is the whole point of {@code toPdfRect}.
     */
    @Input() pages: SmAdHocPageDto[] = [];

    /** The roles a rectangle may be assigned to. See {@link SmPlacementRole} for where they come from. */
    @Input() roles: SmPlacementRole[] = [];

    /**
     * The rectangles already stored for this process, read back through the layout endpoint.
     *
     * Copied into the working list on arrival rather than mutated in place: the host's array is its
     * own, and a tool that edited it would change what the host holds before anything was saved.
     */
    @Input() placements: SmPlacementDraft[] = [];

    /** Blocks drawing and saving while the host is busy. Paging and zooming stay available. */
    @Input() disabled: boolean = false;

    /** True while the host is sending. Shows on the save button and blocks a second send. */
    @Input() saving: boolean = false;

    /** Fired on every change to the working list, so the host can track unsaved work. */
    @Output() placementsChanged = new EventEmitter<SmPlacementDraft[]>();

    /** Fired when the user asks for the list to be stored. Always the COMPLETE list. */
    @Output() save = new EventEmitter<SmManualPlacementDto[]>();

    @ViewChild('pageCanvas') pageCanvas: ElementRef<HTMLCanvasElement>;

    /** The scrolling box around the page. Its width is what 'An Breite anpassen' fits into. */
    @ViewChild('stageViewport') stageViewport: ElementRef<HTMLElement>;

    /** The layer the drag happens on. Its box is what pointer coordinates are measured against. */
    @ViewChild('drawLayer') drawLayer: ElementRef<HTMLElement>;

    // --- Working state ---

    /** The rectangles of this session, in the order they will be sent. */
    drafts: SmPlacementDraft[] = [];

    /** The role the NEXT rectangle is assigned to. Chosen before drawing, changeable per row after. */
    activeRoleKey: string = null;

    // --- Presentation state ---

    /** One-based page currently rendered. */
    pageNumber: number = 1;

    /** Pages the RENDERER opened. Compared against the server's count before anything is drawn. */
    pageCount: number = 0;

    /** Render scale. 1 means 72 dpi, i.e. one PDF point per CSS pixel. */
    zoom: number = 1;

    /** What the page occupies on screen, in CSS pixels. Sizes the draw layer. */
    canvasCssWidth: number = 0;
    canvasCssHeight: number = 0;

    renderState: 'empty' | 'loading' | 'ready' | 'error' = 'empty';

    /** German explanation of a failed render. Null while nothing failed. */
    renderError: string = null;

    /** True where the two readings of the page on screen disagree. Blocks drawing entirely. */
    geometryMismatch: boolean = false;

    /** The placed rectangles that fall on the page currently shown. */
    markers: SmPlacementMarker[] = [];

    /** The box being dragged right now, in CSS pixels. Null while nothing is being dragged. */
    draftBox: SmOverlayRect = null;

    /** What the last refused drag was refused for. Cleared as soon as a new drag starts. */
    drawWarning: string = null;

    /** Zoom bounds. Below the lower one nothing is readable, above the upper one nothing is faster. */
    readonly minZoom: number = 0.25;
    readonly maxZoom: number = 4;

    private initialized: boolean = false;
    private viewReady: boolean = false;

    private pdf: pdfjsLib.PDFDocumentProxy = null;

    /** The document {@link pdf} was opened from. Compared by reference. */
    private loadedDocument: Blob = null;

    /** Guards against a slow load or render landing after a newer one has been started. */
    private loadToken: number = 0;
    private renderToken: number = 0;

    /** The render in flight, so a page or zoom change can cancel it instead of colliding with it. */
    private renderTask: pdfjsLib.RenderTask = null;

    /** The viewport the page on screen is rendered with. The ONE source both directions use. */
    private viewport: pdfjsLib.PageViewport = null;

    /** Backing-store pixels per CSS pixel, measured on the canvas rather than taken off the window. */
    private deviceRatio: number = 1;

    /** Unscaled width of the page on screen, in PDF points. What 'fit width' divides into. */
    private unscaledPageWidth: number = 0;

    /** Where the current drag started, in CSS pixels relative to the draw layer. */
    private dragOriginX: number = 0;
    private dragOriginY: number = 0;
    private dragPointerId: number = null;

    /** Runs the {@link SmPlacementDraft.draftId} of this session. Never sent. */
    private nextDraftId: number = 1;

    ngOnInit(): void {
        this.applyBindings(null);
        this.initialized = true;
    }

    /** The canvas exists only from here on, so a document loaded earlier is drawn now. */
    ngAfterViewInit(): void {

        this.viewReady = true;

        if (this.pdf) {
            void this.renderCurrentPage();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        this.applyBindings(changes);
    }

    ngOnDestroy(): void {
        this.releaseDocument();
    }

    // --- Roles ---

    /** The role select's options. Shows the display name, carries the key. */
    get roleOptions(): { label: string, value: string }[] {
        return (this.roles || []).map(role => ({label: role.displayName, value: role.roleKey}));
    }

    /** What to show for a role key, falling back to the key where no role explains it. */
    roleLabel(roleKey: string): string {

        const role = (this.roles || []).find(candidate => candidate.roleKey === roleKey);

        return role ? role.displayName : roleKey;
    }

    /** Whether drawing is possible at all right now. Every reason it is not is shown on screen. */
    get canDraw(): boolean {

        return !this.disabled
            && !this.saving
            && this.renderState === 'ready'
            && !this.geometryMismatch
            && !!this.activeRoleKey;
    }

    // --- The list ---

    /** The rectangles that sit on the page currently shown. */
    get draftsOnPage(): SmPlacementDraft[] {
        return this.drafts.filter(draft => draft.page === this.pageNumber);
    }

    /** Position of a rectangle in the whole list, one-based - the number the marker carries. */
    ordinalOf(draft: SmPlacementDraft): number {
        return this.drafts.indexOf(draft) + 1;
    }

    /** Removes one rectangle. The list is the state, so this is all a delete has to do. */
    removeDraft(draft: SmPlacementDraft): void {

        const index = this.drafts.indexOf(draft);

        if (index < 0) {
            return;
        }

        this.drafts.splice(index, 1);
        this.afterListChanged();
    }

    /** Moves one rectangle to another role. The rectangle itself does not move. */
    assignRole(draft: SmPlacementDraft, roleKey: string): void {

        draft.roleKey = roleKey;
        this.afterListChanged();
    }

    /** Brings the page of a rectangle on screen, so a row in the list can be found on the document. */
    showDraft(draft: SmPlacementDraft): void {
        this.goToPage(draft.page);
    }

    /** Rectangles that carry no role yet. They cannot be saved, and the list says which. */
    get unassignedCount(): number {
        return this.drafts.filter(draft => !draft.roleKey).length;
    }

    /**
     * Whether the list may be sent.
     *
     * An empty list IS sendable: emptying the placement of a process is a legitimate edit, and the
     * endpoint reconciles rather than appends. What blocks the send is a rectangle without a role,
     * because the server refuses the whole request for it and would take every correct rectangle in
     * the same body down with it.
     */
    get canSave(): boolean {
        return !this.disabled && !this.saving && this.unassignedCount === 0;
    }

    /**
     * Hands the complete list to the host, after checking every rectangle one last time.
     *
     * <p>
     * The crop box check is repeated here rather than trusted from the moment of drawing. A
     * rectangle can only have entered the list through {@link finishDrag}, which checks it - but the
     * list also arrives from the host through {@link placements}, and a rectangle read back from an
     * earlier session, or written by some other tool, has never passed through that path. Sending it
     * would produce a signature nobody can see, and the message would arrive at the customer.
     * </p>
     */
    emitSave(): void {

        if (!this.canSave) {
            return;
        }

        const offending = this.drafts.find(draft => !this.fitsItsPage(draft));

        if (offending) {
            this.drawWarning = 'Das Rechteck Nr. ' + this.ordinalOf(offending) + ' auf Seite '
                + offending.page + ' liegt außerhalb des sichtbaren Bereichs dieser Seite. Eine dort '
                + 'eingefügte Unterschrift wäre unsichtbar. Bitte entfernen Sie das Rechteck und '
                + 'ziehen Sie es erneut auf.';
            return;
        }

        this.drawWarning = null;
        this.save.emit(this.drafts.map(draft => this.toWire(draft)));
    }

    // --- Drawing ---

    /**
     * Starts a drag. Every reason drawing is impossible has already disabled the layer.
     *
     * The pointer is CAPTURED, so a drag that leaves the page still ends where the user let go
     * rather than being abandoned half-drawn - and the clamp is what keeps the resulting rectangle
     * on the page.
     */
    onPointerDown(event: PointerEvent): void {

        if (!this.canDraw) {
            return;
        }

        const layer = this.drawLayer?.nativeElement;

        if (!layer) {
            return;
        }

        event.preventDefault();

        this.drawWarning = null;
        this.dragPointerId = event.pointerId;

        const point = this.pointIn(layer, event);

        this.dragOriginX = point[0];
        this.dragOriginY = point[1];
        this.draftBox = {left: point[0], top: point[1], width: 0, height: 0};

        layer.setPointerCapture(event.pointerId);
    }

    /** Keeps the drawn box under the pointer, cut to the page so it never promises a margin. */
    onPointerMove(event: PointerEvent): void {

        if (this.dragPointerId === null || event.pointerId !== this.dragPointerId) {
            return;
        }

        const layer = this.drawLayer?.nativeElement;

        if (!layer) {
            return;
        }

        const point = this.pointIn(layer, event);

        this.draftBox = clampBoxToPage(
            normaliseDragBox(this.dragOriginX, this.dragOriginY, point[0], point[1]),
            this.canvasCssWidth,
            this.canvasCssHeight);
    }

    /** Ends the drag and turns the box into a rectangle - or explains why it did not. */
    onPointerUp(event: PointerEvent): void {

        if (this.dragPointerId === null || event.pointerId !== this.dragPointerId) {
            return;
        }

        const layer = this.drawLayer?.nativeElement;

        if (layer?.hasPointerCapture(event.pointerId)) {
            layer.releasePointerCapture(event.pointerId);
        }

        this.dragPointerId = null;
        this.finishDrag();
    }

    /**
     * Abandons a drag that was cancelled by the system rather than by the user.
     *
     * Nothing is placed: a cancelled drag has no end point, and inventing one from the last position
     * would place a rectangle the user never finished drawing.
     */
    onPointerCancel(): void {
        this.dragPointerId = null;
        this.draftBox = null;
    }

    /**
     * Turns the drawn box into a stored rectangle, or refuses it and says why.
     *
     * <h3>Order of the checks, and why it is this one</h3>
     * <p>
     * Size first, because a stray click is by far the most common outcome and deserves no message at
     * all. Then the conversion, which can fail only on a collapsed viewport. Then the crop box, and
     * only then the size again - this time in PDF points, which is the unit the backend judges in
     * and the only one that stays honest across zoom levels.
     * </p>
     */
    private finishDrag(): void {

        const box = this.draftBox;

        this.draftBox = null;

        if (!box) {
            return;
        }

        // A click rather than a drag. Silent on purpose: it is how people put focus somewhere, and
        // an error message for it would fire constantly.
        if (box.width < 1 || box.height < 1) {
            return;
        }

        const rect = toPdfRect(fromCssPixels(box, this.deviceRatio), this.viewport);

        if (!rect) {
            this.drawWarning = 'Die Position auf dem Dokument konnte nicht bestimmt werden. Bitte '
                + 'bauen Sie die Seite über „Seite neu aufbauen“ erneut auf.';
            return;
        }

        const reported = this.reportedPage();

        if (!isInsideCropBox(rect, reported)) {
            // Should be unreachable: the drag was clamped to the rendered page. Kept because the
            // clamp is only as good as the extent it was given, and a rectangle in the cropped-away
            // margin fails INVISIBLY - the stamp succeeds and the document comes back unsigned.
            this.drawWarning = 'Das Rechteck liegt außerhalb des sichtbaren Bereichs der Seite. Eine '
                + 'dort eingefügte Unterschrift wäre unsichtbar. Bitte ziehen Sie das Rechteck '
                + 'vollständig innerhalb der Seite auf.';
            return;
        }

        if (rect.width < SM_MIN_PLACEMENT_SIZE_PT || rect.height < SM_MIN_PLACEMENT_SIZE_PT) {
            this.drawWarning = 'Das Rechteck ist zu klein für eine lesbare Unterschrift. Bitte ziehen '
                + 'Sie es mindestens ' + SM_MIN_PLACEMENT_SIZE_PT + ' Punkte breit und hoch auf.';
            return;
        }

        this.drafts.push({
            draftId: this.nextDraftId++,
            roleKey: this.activeRoleKey,
            page: this.pageNumber,
            rect: rect
        });

        this.afterListChanged();
    }

    /** Where a pointer sits inside an element, in CSS pixels, independent of what it is over. */
    private pointIn(element: HTMLElement, event: PointerEvent): number[] {

        const bounds = element.getBoundingClientRect();

        return [event.clientX - bounds.left, event.clientY - bounds.top];
    }

    // --- Page navigation ---

    get canGoBack(): boolean {
        return this.pageNumber > 1;
    }

    get canGoForward(): boolean {
        return this.pageNumber < this.pageCount;
    }

    /** Every page as a jump destination, so a long document is one click away from any page. */
    get pageOptions(): { label: string, value: number }[] {

        const options: { label: string, value: number }[] = [];

        for (let page = 1; page <= this.pageCount; page++) {
            options.push({label: 'Seite ' + page, value: page});
        }

        return options;
    }

    goToPreviousPage(): void {

        if (!this.canGoBack) {
            return;
        }

        this.pageNumber--;
        void this.renderCurrentPage();
    }

    goToNextPage(): void {

        if (!this.canGoForward) {
            return;
        }

        this.pageNumber++;
        void this.renderCurrentPage();
    }

    goToPage(page: number): void {

        if (!page || page < 1 || page > this.pageCount || page === this.pageNumber) {
            return;
        }

        this.pageNumber = page;
        void this.renderCurrentPage();
    }

    /** Draws the current page again - the recovery after a failed render. */
    retryRender(): void {
        void this.renderCurrentPage();
    }

    // --- Zoom ---

    get zoomPercent(): number {
        return Math.round(this.zoom * 100);
    }

    zoomIn(): void {
        this.applyZoom(this.zoom * 1.25);
    }

    zoomOut(): void {
        this.applyZoom(this.zoom / 1.25);
    }

    /** Scales the page so it exactly fills the available width. Recomputed, never remembered. */
    fitWidth(): void {

        const element = this.stageViewport?.nativeElement;

        if (!element || !this.unscaledPageWidth) {
            return;
        }

        const style = getComputedStyle(element);
        const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
        const available = element.clientWidth - padding;

        if (available <= 0) {
            return;
        }

        this.applyZoom(available / this.unscaledPageWidth);
    }

    private applyZoom(zoom: number): void {

        const next = Math.min(this.maxZoom, Math.max(this.minZoom, zoom));

        if (Math.abs(next - this.zoom) < 0.001) {
            return;
        }

        this.zoom = next;
        void this.renderCurrentPage();
    }

    // --- Private: binding ---

    private applyBindings(changes: SimpleChanges): void {

        const firstPass = !changes;

        if (this.document !== this.loadedDocument) {
            void this.loadDocument();
        }

        // The host's list is COPIED, never adopted. Editing the array the host holds would change
        // what it believes is stored before anything has been sent, and the rectangles themselves
        // are copied too so a role change here does not reach back into it.
        if (firstPass || changes['placements']) {
            this.adoptPlacements();
            this.refreshMarkers();
        }

        // One role means there is nothing to choose. Preselecting it removes a step that could only
        // ever have one answer; several roles are deliberately NOT preselected, because guessing
        // which one a caseworker meant is how a rectangle ends up under the wrong signer.
        if (firstPass || changes['roles']) {

            const available = this.roles || [];

            if (this.activeRoleKey && !available.some(role => role.roleKey === this.activeRoleKey)) {
                this.activeRoleKey = null;
            }

            if (!this.activeRoleKey && available.length === 1) {
                this.activeRoleKey = available[0].roleKey;
            }
        }
    }

    /** Takes over the host's list as this session's own, rectangles included. */
    private adoptPlacements(): void {

        this.drafts = (this.placements || []).map(placement => ({
            draftId: this.nextDraftId++,
            roleKey: placement.roleKey,
            page: placement.page,
            rect: {...placement.rect}
        }));
    }

    /** Everything that has to happen after the list changed, in one place. */
    private afterListChanged(): void {
        this.refreshMarkers();
        this.placementsChanged.emit(this.drafts);
    }

    /** The wire shape: the four corners, flat and unrounded. */
    private toWire(draft: SmPlacementDraft): SmManualPlacementDto {

        return {
            roleKey: draft.roleKey,
            page: draft.page,
            lowerLeftX: draft.rect.lowerLeftX,
            lowerLeftY: draft.rect.lowerLeftY,
            upperRightX: draft.rect.upperRightX,
            upperRightY: draft.rect.upperRightY
        };
    }

    /** Whether a stored rectangle still lies inside the crop box of the page it names. */
    private fitsItsPage(draft: SmPlacementDraft): boolean {

        const page = (this.pages || []).find(candidate => candidate.page === draft.page);

        // No geometry for that page means it cannot be judged. Treated as fitting, because refusing
        // to save on missing evidence would block a correct list for want of a check - the backend
        // still holds the refusal that matters.
        return !page || isInsideCropBox(draft.rect, page);
    }

    /** The geometry the server reported for the page on screen, or null where it reported none. */
    private reportedPage(): SmAdHocPageDto {
        return (this.pages || []).find(page => page.page === this.pageNumber) || null;
    }

    // --- Private: document and rendering ---

    private async loadDocument(): Promise<void> {

        this.releaseDocument();
        this.loadedDocument = this.document;

        if (!this.document) {
            this.renderState = 'empty';
            return;
        }

        const token = ++this.loadToken;

        this.renderState = 'loading';
        this.renderError = null;

        try {

            pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerSrc();

            const bytes = new Uint8Array(await this.document.arrayBuffer());
            const pdf = await pdfjsLib.getDocument({data: bytes}).promise;

            if (token !== this.loadToken) {
                void pdf.destroy();
                return;
            }

            this.pdf = pdf;
            this.pageCount = pdf.numPages;
            this.pageNumber = 1;
            this.zoom = 1;

            await this.renderCurrentPage();

        } catch (error) {

            if (token !== this.loadToken) {
                return;
            }

            this.renderState = 'error';
            this.renderError = 'Das Dokument konnte nicht angezeigt werden. Bitte prüfen Sie, ob es '
                + 'sich um eine lesbare PDF-Datei handelt.';
        }
    }

    /**
     * Draws {@link pageNumber} and rebuilds the boxes on it.
     *
     * <p>
     * The viewport is fetched ANEW on every pass and every box is recomputed from it, in both
     * directions. The draw layer is never scaled by a transform of its own: it and the canvas would
     * then be two independently scaled things that agree only by luck, and a rectangle drawn on a
     * layer that had drifted would be stored in coordinates nobody could see was wrong.
     * </p>
     */
    private async renderCurrentPage(): Promise<void> {

        if (!this.pdf || !this.viewReady) {
            return;
        }

        const token = ++this.renderToken;

        this.renderState = 'loading';
        this.renderError = null;
        this.draftBox = null;
        this.dragPointerId = null;

        try {

            // The previous render is asked to stop before anything touches the canvas: pdf.js
            // permits one render at a time per canvas and per page proxy.
            this.stopCurrentRender();

            const page = await this.pdf.getPage(this.pageNumber);

            if (token !== this.renderToken) {
                return;
            }

            const canvas = this.pageCanvas?.nativeElement;

            if (!canvas) {
                this.renderState = 'empty';
                return;
            }

            const viewport = page.getViewport({scale: this.zoom});

            this.unscaledPageWidth = page.getViewport({scale: 1}).width;

            canvas.width = Math.max(1, Math.round(viewport.width));
            canvas.height = Math.max(1, Math.round(viewport.height));
            canvas.style.width = `${canvas.width}px`;
            canvas.style.height = `${canvas.height}px`;

            // pdf.js 5 takes the canvas itself; canvasContext is only accepted when canvas is null.
            const task = page.render({canvas: canvas, viewport: viewport});

            this.renderTask = task;

            await task.promise;

            if (token !== this.renderToken) {
                return;
            }

            this.renderTask = null;
            this.viewport = viewport;
            this.deviceRatio = deviceRatioOf(canvas.width, canvas.clientWidth);
            this.canvasCssWidth = canvas.clientWidth || Math.round(viewport.width / this.deviceRatio);
            this.canvasCssHeight = canvas.clientHeight || Math.round(viewport.height / this.deviceRatio);

            this.checkPageGeometry(page);
            this.refreshMarkers();

            this.renderState = 'ready';

        } catch (error) {

            if (token !== this.renderToken || this.isCancellation(error)) {
                return;
            }

            this.renderState = 'error';
            this.renderError = 'Diese Seite konnte nicht aufgebaut werden. Bitte bauen Sie sie über '
                + '„Seite neu aufbauen“ erneut auf oder wechseln Sie die Seite.';
        }
    }

    /**
     * Compares the renderer's reading of the page with the server's - and blocks drawing where they
     * disagree.
     *
     * <p>
     * Reuses {@code buildPageOverlay}, so the check and the boxes come from one place. A
     * disagreement means the two are looking at different views of the same document: a rectangle
     * drawn now would be stored in coordinates that mean something else on the server, and unlike a
     * misplaced marker it would persist and be stamped.
     * </p>
     */
    private checkPageGeometry(page: pdfjsLib.PDFPageProxy): void {

        const measured: SmPdfPageMeasurement = {
            pageNumber: this.pageNumber,
            widthPt: page.view[2] - page.view[0],
            heightPt: page.view[3] - page.view[1],
            rotation: page.rotate
        };

        // An empty probe rectangle: buildPageOverlay answers null for a disagreement and an empty
        // layer for a page that simply carries nothing, which is exactly the distinction wanted here.
        this.geometryMismatch = buildPageOverlay(
            [], this.pages, this.pageCount, measured, this.viewport) === null;
    }

    /** Rebuilds the boxes shown on the page currently rendered. */
    private refreshMarkers(): void {

        if (!this.viewport || this.geometryMismatch) {
            this.markers = [];
            return;
        }

        const onPage = this.draftsOnPage;
        const rects: SmAdHocRectDto[] = onPage.map(draft => draft.rect);

        this.markers = rects.map((rect, index) => {

            // The SAME forward conversion the guided view draws its markers with. The geometry file
            // owns both directions; nothing here recomputes either of them.
            const css = toCssPixels(toOverlayRect(rect, this.viewport), this.deviceRatio);

            return {
                draft: onPage[index],
                ordinal: this.ordinalOf(onPage[index]),
                left: css.left,
                top: css.top,
                width: css.width,
                height: css.height
            };
        });
    }

    /** Asks the render in flight to stop. It owns the canvas until it lets go. */
    private stopCurrentRender(): void {

        const task = this.renderTask;

        this.renderTask = null;

        if (!task) {
            return;
        }

        try {
            task.cancel();
        } catch (error) {
            // Already finished. Cancelling a completed task is not an error worth reporting.
        }
    }

    /** Whether a rejection is pdf.js cancelling the task rather than failing at it. */
    private isCancellation(error: unknown): boolean {
        return (error as { name?: string })?.name === 'RenderingCancelledException';
    }

    private releaseDocument(): void {

        this.stopCurrentRender();

        const pdf = this.pdf;

        this.pdf = null;
        this.viewport = null;
        this.markers = [];

        if (pdf) {
            void Promise.resolve(pdf.destroy()).catch(() => undefined);
        }
    }
}
